import { useState } from 'react';

interface HeaderProps {
  isScrolled: boolean;
}

export default function Header({ isScrolled }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const menuItems = [
    { name: 'Home', href: '#home' },
    { name: 'About', href: '#about' },
    { name: 'Products', href: '#products' },
    { name: 'Testimonials', href: '#testimonials' },
    { name: 'Gallery', href: '#gallery' },
    { name: 'Contact', href: '#contact' },
  ];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="container mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <div className="flex-shrink-0 cursor-pointer" onClick={() => scrollToSection('hero')}>
            <img
              src="https://static.readdy.ai/image/8881b4115d4dda1b14392e631a2bc850/5b000b06795f3578a16954d30644be73.png"
              alt="Manohar International Logo"
              className="h-12 w-auto"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('hero')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Home
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('products')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Products
            </button>
            <button
              onClick={() => scrollToSection('testimonials')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Testimonials
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className={`text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                isScrolled ? 'text-gray-700 hover:text-teal-600' : 'text-white hover:text-teal-300'
              }`}
            >
              Contact
            </button>
          </nav>

          {/* Social Media Links */}
          <div className="hidden lg:flex items-center space-x-4">
            <a
              href="https://facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'text-gray-700 hover:bg-teal-600 hover:text-white'
                  : 'text-white hover:bg-white/20'
              }`}
            >
              <i className="ri-facebook-fill text-lg"></i>
            </a>
            <a
              href="https://twitter.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'text-gray-700 hover:bg-teal-600 hover:text-white'
                  : 'text-white hover:bg-white/20'
              }`}
            >
              <i className="ri-twitter-x-fill text-lg"></i>
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'text-gray-700 hover:bg-teal-600 hover:text-white'
                  : 'text-white hover:bg-white/20'
              }`}
            >
              <i className="ri-linkedin-fill text-lg"></i>
            </a>
            <a
              href="https://instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-9 h-9 flex items-center justify-center rounded-full transition-colors cursor-pointer ${
                isScrolled
                  ? 'text-gray-700 hover:bg-teal-600 hover:text-white'
                  : 'text-white hover:bg-white/20'
              }`}
            >
              <i className="ri-instagram-fill text-lg"></i>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${
              isScrolled ? 'text-gray-700' : 'text-white'
            }`}
          >
            <i className={`text-2xl ${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-white shadow-lg rounded-lg mt-2 py-4 px-6 mb-4">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('hero')}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                Home
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                About
              </button>
              <button
                onClick={() => scrollToSection('products')}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                Products
              </button>
              <button
                onClick={() => scrollToSection('testimonials')}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                Testimonials
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors text-left whitespace-nowrap cursor-pointer"
              >
                Contact
              </button>
            </nav>
            <div className="flex items-center space-x-4 mt-6 pt-4 border-t border-gray-200">
              <a
                href="https://facebook.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-gray-700 hover:bg-teal-600 hover:text-white rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-facebook-fill text-lg"></i>
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-gray-700 hover:bg-teal-600 hover:text-white rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-twitter-x-fill text-lg"></i>
              </a>
              <a
                href="https://linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-gray-700 hover:bg-teal-600 hover:text-white rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-linkedin-fill text-lg"></i>
              </a>
              <a
                href="https://instagram.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center text-gray-700 hover:bg-teal-600 hover:text-white rounded-full transition-colors cursor-pointer"
              >
                <i className="ri-instagram-fill text-lg"></i>
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
